/**
 * Builder Read Slide Tool
 * 
 * Tool for reading slides from the working presentation file during builds.
 * Supports two types of slides with automatic index resolution:
 * 
 * - styleGuideSlides: Hidden reference slides at the START of the presentation
 * - workingSlides: Content slides being built AFTER style guide slides
 * 
 * Index Resolution:
 * - Style guide slide 1 → PPTX index 0
 * - Style guide slide 20 → PPTX index 19
 * - Working slide 1 → PPTX index (styleGuideCount + 0)
 * - Working slide 5 → PPTX index (styleGuideCount + 4)
 * 
 * The agent just says "working slide 5" without thinking about offsets.
 * 
 * Images are stored in contextResolver and returned via _imageIds for
 * automatic injection in the next user message (MessageBuilder pattern).
 */

import { ToolDescription, safeParseToolParams } from "../../../base/toolDescription";
import { ToolBase } from "../../../base/toolBase";
import { backendClient } from "../../../../../services/communication/backendClient";
import GlobalStateManager from "../../../../globals/global-state/globalStateManager";
import {
    BuilderReadSlideParamsSchema,
    type BuilderReadSlideParams,
    type BuilderReadSlideResponse,
    type BuilderSlideResult,
    type BuilderShapeContent,
    type BuilderDetailLevel,
} from "./builderReadSlide.schema";
import { extractBase64FromDataUrl } from "../../prompts";

// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const BUILDER_READ_SLIDE_SYSTEM_PROMPT = `### readSlide
Read slides from the presentation during builds.

**Two types of slides:**
1. **styleGuideSlides** - Hidden reference slides for styling/layout
2. **workingSlides** - Content slides you're building

**Detail levels:**
- \`layout\` - Fast: positions, text content, basic structure
- \`complete\` - Full: all properties including provenance

\`\`\`json
{
  "workingSlides": { "indexes": [1], "detailLevel": "complete", "includeImage": true },
  "styleGuideSlides": { "indexes": [5], "detailLevel": "layout" }
}
\`\`\`

**Always read before building** to understand current state.`;

// ═══════════════════════════════════════════════════════════════════════════
// BUILDER READ SLIDE TOOL
// ═══════════════════════════════════════════════════════════════════════════

export class BuilderReadSlideTool extends ToolBase<BuilderReadSlideParams, BuilderReadSlideResponse> {
    /** Tool name shown to the agent */
    public readonly name = "readSlide";
    public readonly description: ToolDescription;
    
    // System prompt description
    public readonly systemPromptDescription = BUILDER_READ_SLIDE_SYSTEM_PROMPT;
    
    /** Plan ID for this build session */
    private planId: string;
    
    /** Number of style guide slides (offset for working slides) */
    private styleGuideSlideCount: number;
    
    /**
     * Create a BuilderReadSlideTool
     * 
     * @param planId - The plan ID for this build
     * @param styleGuideSlideCount - Number of style guide slides (from build init)
     */
    constructor(planId: string, styleGuideSlideCount: number) {
        super();
        this.planId = planId;
        this.styleGuideSlideCount = styleGuideSlideCount;
        
        this.description = new ToolDescription(
            "readSlide",
            `Read slides from the presentation to see their current state.

Two types of slides are available:
1. **styleGuideSlides**: Hidden reference slides (for styling/layout reference)
   - ${styleGuideSlideCount} style guide slides available (indices 1-${styleGuideSlideCount})
2. **workingSlides**: Content slides you're building

Detail levels:
- "layout": Fast scan - positions, text content, basic structure
- "complete": Full - all properties including provenance

Example - read working slide 1 and style guide slide 5:
{
  "workingSlides": { "indexes": [1], "detailLevel": "complete" },
  "styleGuideSlides": { "indexes": [5], "detailLevel": "layout" }
}

Returns slide data and images for visual inspection.`,
            BuilderReadSlideParamsSchema
        );
    }
    
    /**
     * Execute the tool
     */
    public async execute(parameters: unknown): Promise<BuilderReadSlideResponse> {
        const parseResult = safeParseToolParams(BuilderReadSlideParamsSchema, parameters);
        
        if (!parseResult.success) {
            console.error('[BuilderReadSlideTool] Invalid parameters:', parseResult.error);
            return {
                success: false,
                error: `Invalid parameters: ${parseResult.error}`,
            };
        }
        
        const params = parseResult.data;
        console.log(`[BuilderReadSlideTool] Reading slides:`, {
            workingSlides: params.workingSlides?.indexes,
            styleGuideSlides: params.styleGuideSlides?.indexes,
        });
        
        const globalState = GlobalStateManager.getInstance();
        const imageIds: string[] = [];
        
        const workingSlideResults: BuilderSlideResult[] = [];
        const styleGuideSlideResults: BuilderSlideResult[] = [];
        
        try {
            // Read working slides
            if (params.workingSlides) {
                const results = await this.readSlides(
                    params.workingSlides.indexes,
                    params.workingSlides.detailLevel || 'complete',
                    params.workingSlides.includeImages ?? true,
                    'working',
                    globalState,
                    imageIds
                );
                workingSlideResults.push(...results);
            }
            
            // Read style guide slides
            if (params.styleGuideSlides) {
                const results = await this.readSlides(
                    params.styleGuideSlides.indexes,
                    params.styleGuideSlides.detailLevel || 'complete',
                    params.styleGuideSlides.includeImages ?? true,
                    'styleGuide',
                    globalState,
                    imageIds
                );
                styleGuideSlideResults.push(...results);
            }
            
            console.log(`[BuilderReadSlideTool] Read ${workingSlideResults.length} working slides, ${styleGuideSlideResults.length} style guide slides`);
            
            return {
                success: true,
                workingSlides: workingSlideResults.length > 0 ? workingSlideResults : undefined,
                styleGuideSlides: styleGuideSlideResults.length > 0 ? styleGuideSlideResults : undefined,
                _imageIds: imageIds.length > 0 ? imageIds : undefined,
            };
            
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            console.error(`[BuilderReadSlideTool] Error:`, errorMessage);
            
            return {
                success: false,
                workingSlides: workingSlideResults.length > 0 ? workingSlideResults : undefined,
                styleGuideSlides: styleGuideSlideResults.length > 0 ? styleGuideSlideResults : undefined,
                error: errorMessage,
            };
        }
    }
    
    /**
     * Read multiple slides of a given type
     */
    private async readSlides(
        userIndexes: number[],
        detailLevel: BuilderDetailLevel,
        includeImages: boolean,
        slideType: 'working' | 'styleGuide',
        globalState: GlobalStateManager,
        imageIds: string[]
    ): Promise<BuilderSlideResult[]> {
        const results: BuilderSlideResult[] = [];
        
        for (const userIndex of userIndexes) {
            // Convert user's 1-based index to actual PPTX 0-based index
            const pptxIndex = this.resolveIndex(userIndex, slideType);
            
            console.log(`[BuilderReadSlideTool] Reading ${slideType} slide ${userIndex} → PPTX index ${pptxIndex}`);
            
            try {
                // Call backend to read the slide
                // TODO: Pass detailLevel once backend supports it
                const response = await backendClient.readWorkingSlide(this.planId, pptxIndex);
                
                if (!response.success) {
                    results.push({
                        slideIndex: userIndex,
                        isHidden: false,
                        shapes: [],
                        error: response.error || 'Failed to read slide',
                    });
                    continue;
                }
                
                // Map response to our schema
                const slideData = response.slide_data;
                const result: BuilderSlideResult = {
                    slideIndex: userIndex,  // Return the user's index, not PPTX index
                    layoutName: slideData?.layout_name,
                    isHidden: slideData?.is_hidden ?? false,
                    customTags: slideData?.custom_tags,
                    shapes: this.mapShapes(slideData?.shapes || [], detailLevel),
                };
                
                // Store image in contextResolver if requested
                if (includeImages && response.screenshot_base64) {
                    const imageId = `${slideType}_slide_${userIndex}_${Date.now()}`;
                    
                    // Extract raw base64 from data URL using utility (handles edge cases)
                    const base64Data = extractBase64FromDataUrl(response.screenshot_base64);
                    
                    await globalState.contextResolver.storeImageAsync(
                        imageId,
                        base64Data,
                        'image/png',
                        {
                            slideType,
                            userIndex,
                            pptxIndex,
                            planId: this.planId,
                        }
                    );
                    
                    imageIds.push(imageId);
                    console.log(`[BuilderReadSlideTool] Stored image: ${imageId}`);
                }
                
                results.push(result);
                
            } catch (error) {
                const errorMsg = error instanceof Error ? error.message : String(error);
                console.error(`[BuilderReadSlideTool] Error reading ${slideType} slide ${userIndex}:`, errorMsg);
                
                results.push({
                    slideIndex: userIndex,
                    isHidden: false,
                    shapes: [],
                    error: errorMsg,
                });
            }
        }
        
        return results;
    }
    
    /**
     * Resolve user's 1-based index to actual PPTX 0-based index
     * 
     * Style guide slides: indices 1-N map to PPTX 0 to N-1
     * Working slides: index 1 maps to PPTX styleGuideCount, index 2 to styleGuideCount+1, etc.
     */
    private resolveIndex(userIndex: number, slideType: 'working' | 'styleGuide'): number {
        if (slideType === 'styleGuide') {
            // Style guide slide N (1-based) → PPTX index N-1
            return userIndex - 1;
        } else {
            // Working slide N (1-based) → PPTX index styleGuideCount + (N-1)
            return this.styleGuideSlideCount + (userIndex - 1);
        }
    }
    
    /**
     * Map shapes from backend format, filtering by detail level
     * 
     * Backend returns snake_case, we convert to camelCase for TypeScript.
     */
    private mapShapes(shapes: BackendShape[], detailLevel: BuilderDetailLevel): BuilderShapeContent[] {
        return shapes.map(shape => {
            // Base properties (all detail levels)
            const mapped: BuilderShapeContent = {
                shapeId: shape.shape_id,
                name: shape.name,
                type: shape.shape_type || shape.type,
                position: shape.position,
            };
            
            // Text content
            if (shape.text) mapped.text = shape.text;
            if (shape.paragraphs) mapped.paragraphs = shape.paragraphs;
            
            // Table info
            if (shape.table_info) mapped.tableInfo = shape.table_info;
            
            // Chart info
            if (shape.chart_info) mapped.chartInfo = shape.chart_info;
            
            // Complete level: add formatting
            if (detailLevel === 'complete') {
                if (shape.fill) mapped.fill = shape.fill;
                if (shape.outline) mapped.outline = shape.outline;
            }
            
            // Complete level: add everything
            if (detailLevel === 'complete') {
                if (shape.carousel_metadata) mapped.carouselMetadata = shape.carousel_metadata;
            }
            
            return mapped;
        });
    }
}

/**
 * Backend shape format (snake_case)
 * This is a simplified type for the shape data returned by the Python backend.
 * Uses Record<string, unknown> for complex nested structures since the backend
 * may return varying shapes of data.
 */
interface BackendShape {
    shape_id: string;
    name?: string;
    type?: string;
    shape_type?: string;
    position: { left: number; top: number; width: number; height: number };
    text?: string;
    paragraphs?: Array<{ text: string; level: number }>;
    fill?: Record<string, unknown>;
    outline?: Record<string, unknown>;
    table_info?: Record<string, unknown>;
    chart_info?: Record<string, unknown>;
    carousel_metadata?: Record<string, unknown>;
}

export default BuilderReadSlideTool;
